import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { CreditCard } from '../CreditCard';
import { CreditCardRewardsType } from '../CreditCardRewardsTypes';
import { CreditCardData } from '../CreditCardData';


export const usePointValues = (
  cards: CreditCard[],
  enableOverrides: boolean = false,
  setOverrides: (overrides: Record<string, number> | ((prev: Record<string, number>) => Record<string, number>)) => void,
  overrides: Record<string, number>,
): {
  pointValues: Record<string, number>;
  setPointValues: (rewardsType: CreditCardRewardsType, value: string) => void;
  updateOverrides: (rewardsType: CreditCardRewardsType, value: string) => void;
  overrideKeys: string[];
  pointValuesForCards: Record<string, number>;
  resetPointValues: (_?: CreditCardRewardsType) => void;
} => {
  const rewardsTypes: CreditCardRewardsType[] = useMemo(() => {
    const result= _.uniq(cards.map((card) => CreditCardData[card].rewardsType));
      console.warn('rewardsTypes', result);
    return result;
  }, [cards]);

  const pointValues: Partial<Record<CreditCardRewardsType, number>> = useMemo(() => {
    // Get any rewards types that are not necessarily part of CreditCardRewardsType
    const otherRewardsTypes = cards
      .map((card) => CreditCardData[card].rewardsType)
      .filter(
        (rewardsType) =>
          !Object.values(CreditCardRewardsType).includes(rewardsType)
      );
    if (otherRewardsTypes.length > 0) {
      console.warn('otherRewardsTypes', otherRewardsTypes);
    }
    return {
      [CreditCardRewardsType.CASH_BACK]: 1.0,
      [CreditCardRewardsType.CHASE_UR]:
        cards.includes(CreditCard.CHASE_SAPPHIRE_RESERVE_CREDIT_CARD) ||
        cards.includes(CreditCard.CHASE_SAPPHIRE_PREFERRED_CREDIT_CARD)
          ? 2.05
          : 1,
      [CreditCardRewardsType.AMEX_MR]: 2.0,
      [CreditCardRewardsType.AVIOS]: 1.5,
      [CreditCardRewardsType.CITI_TYP]: 1.8,
      [CreditCardRewardsType.CAPITAL_ONE]: 1.85,
      [CreditCardRewardsType.BILT_REWARDS]: 2.05,
      [CreditCardRewardsType.WELLS_FARGO]: 1.6,
      [CreditCardRewardsType.MARRIOTT_BONVOY]: 0.85,
      [CreditCardRewardsType.HILTON]: 0.6,
      [CreditCardRewardsType.UNITED]: 1.35,
      [CreditCardRewardsType.ALASKA]: 1.4,
      [CreditCardRewardsType.AMERICAN]: 1.6,
      [CreditCardRewardsType.SOUTHWEST]: 1.4,
      [CreditCardRewardsType.JETBLUE]: 1.3,
      [CreditCardRewardsType.HAWAIIAN]: 1.2,
      [CreditCardRewardsType.FRONTIER]: 1.1,
      // [CreditCardRewardsType.BEST_WESTERN]: 0.6,
      [CreditCardRewardsType.CHOICE]: 0.6,
      [CreditCardRewardsType.HYATT]: 1.7,
      [CreditCardRewardsType.IHG]: 0.5,
      [CreditCardRewardsType.WYNDHAM]: 1.1,
      ...(enableOverrides ? overrides : {}),
    };
  }, [overrides, enableOverrides, cards]);

  const updateOverrides = useCallback(
    (rewardsType: CreditCardRewardsType, value: string) => {
      if (Number(value) !== pointValues[rewardsType]) {
        setOverrides((prevPointValues: Record<string, number>) => {
          return {
            ...prevPointValues,
            [rewardsType]: Number(value),
          };
        });
      }
    },
    [setOverrides, pointValues]
  );

  const resetPointValues = useCallback(
    (key?: CreditCardRewardsType) => {
      if (key) {
        setOverrides(
          (prevPointValues: Partial<Record<CreditCardRewardsType, number>>) => {
            const { [key]: _, ...rest } = prevPointValues;
            return rest;
          }
        );
      } else {
        setOverrides({});
      }
    },
    [setOverrides]
  );

  return {
    pointValues,
    pointValuesForCards: useMemo(() => _.pick(pointValues, rewardsTypes), [pointValues, rewardsTypes]),
    setPointValues: updateOverrides,
    updateOverrides,
    overrideKeys: Object.keys(overrides),
    resetPointValues,
  };
};
